import type { ComponentProps } from 'react'
import HotelMapMarker from 'components/Pages/HotelSearchPage/HotelMapMarker/HotelMapMarker'
import useOfferMetaData from 'hooks/Offers/useOfferMetaData'
import { useSearchPrices } from 'hooks/Search/useSearchPrices'
import useSearchBurnEnabled from 'hooks/useSearchBurnEnabled'
import { useCurrencyFormatter } from 'hooks/useNumberFormatters'
import { useAppSelector } from 'hooks/reduxHooks'
import { getShouldUseSearchPrices } from 'lib/search/searchUtils'
import { hasDisplayablePackagePrice, isDisplayablePrice } from 'lib/offer/pricingVisibility'
import useLuxPlusDisplayedMode from 'luxPlus/hooks/useLuxPlusDisplayedMode'
import getLuxPlusPrimaryCashPrice from 'luxPlus/lib/getLuxPlusPrimaryCashPrice'
import { hasPassedHidePricing, hasPassedWalledGarden, isGoogleHotelAdsBot } from 'selectors/accountSelectors'
import { isGhaPriceDisplay } from 'selectors/routerSelectors'
import useSearchMapCardFilters from './useSearchMapCardFilters'

interface Props extends ComponentProps<typeof HotelMapMarker> {
  offer: App.HotelOffer | App.HotelOfferSummary
}

/** Use the Flash tile's pricing and visibility rules for its marker. */
export default function SearchMapFlashPriceMarker({ offer, priceLabel, ...markerProps }: Props) {
  const { filters: searchFilters, soldOut } = markerProps
  const filters = useSearchMapCardFilters(offer.id, searchFilters)
  const useRealSearchPrices = getShouldUseSearchPrices(filters)
  const metadata = useOfferMetaData(offer.id, filters)
  const { defaultPricing, memberPricing, hidePrice } = useSearchPrices({
    offer,
    pkg: offer.lowestPricePackage,
    duration: metadata?.pricing?.duration ?? offer.lowestPricePackage?.duration,
    filters,
    useRealSearchPrices,
    showPerNightPricing: markerProps.showPerNightPricing,
  })
  const price = defaultPricing.price
  const memberPrice = memberPricing?.price ?? 0
  const displayedMode = useLuxPlusDisplayedMode({ price, memberPrice, offerConfig: offer.luxPlus })
  const isGoogleHotelAds = useAppSelector(isGoogleHotelAdsBot)
  const isGhaPricing = useAppSelector(isGhaPriceDisplay)
  const passedHidePricing = useAppSelector(hasPassedHidePricing)
  const passedWalledGarden = useAppSelector(hasPassedWalledGarden)
  const { hasSelectedBurnToggle } = useSearchBurnEnabled()
  const formatCurrency = useCurrencyFormatter()
  const displayPrice = getLuxPlusPrimaryCashPrice(price, memberPrice, displayedMode, isGoogleHotelAds, isGhaPricing)
  const canShowCashPrice = !soldOut && !hidePrice && hasDisplayablePackagePrice(offer.lowestPricePackage) &&
    !filters.viewPriceInPoints && !hasSelectedBurnToggle &&
    Number.isFinite(displayPrice) && displayPrice > 0
  const canShowWalledPrice = !offer.walledGarden || passedHidePricing || passedWalledGarden
  const hasSearchPrice = !useRealSearchPrices || (metadata?.available === true && isDisplayablePrice(metadata.pricing?.lowestPrice))
  const fallbackLabel = canShowCashPrice ? formatCurrency(displayPrice, 'roundedDollar') : undefined
  // Use a row's label only when its search matches the marker's confirmed pricing.
  const rowPriceLabel = filters === searchFilters ? priceLabel : undefined

  return <HotelMapMarker
    {...markerProps}
    priceLabel={canShowWalledPrice && hasSearchPrice ? rowPriceLabel ?? fallbackLabel : undefined}
  />
}
